import { type ReactNode, useEffect, useId, useRef, useState } from 'react';

interface ConfirmActionProps {
  buttonLabel: string;
  children: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  disabled?: boolean;
  onConfirm(): unknown | Promise<unknown>;
}

export function ConfirmAction({ buttonLabel, children, confirmLabel = 'Confirm', danger = false, disabled = false, onConfirm }: ConfirmActionProps) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const cancel = useRef<HTMLButtonElement>(null);
  const inFlight = useRef(false);
  const titleId = useId();
  const bodyId = useId();

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open && !element.open) { element.showModal(); cancel.current?.focus(); }
    if (!open && element.open) { element.close(); trigger.current?.focus(); }
  }, [open]);

  async function confirm() {
    if (inFlight.current) return;
    inFlight.current = true;
    setPending(true); setFailed(false);
    try { await onConfirm(); setOpen(false); }
    catch { setFailed(true); }
    finally { inFlight.current = false; setPending(false); }
  }

  return <>
    <button ref={trigger} className={danger ? 'button button--danger' : 'button'} disabled={disabled} onClick={() => { setFailed(false); setOpen(true); }} type="button">{buttonLabel}</button>
    <dialog ref={dialog} aria-labelledby={titleId} aria-describedby={bodyId} aria-busy={pending} className="confirm-modal"
      onKeyDown={event => {
        if (event.key !== 'Tab') return;
        const controls = Array.from(event.currentTarget.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]'));
        const first = controls[0], last = controls[controls.length - 1];
        if (!first) event.preventDefault();
        else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }}
      onCancel={event => { event.preventDefault(); if (!inFlight.current) setOpen(false); }}
      onClose={() => setOpen(false)}
      onClick={event => {
        if (event.target !== dialog.current || inFlight.current) return;
        const rect = dialog.current.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) setOpen(false);
      }}>
      <h2 id={titleId}>{buttonLabel}</h2>
      <div id={bodyId} className="confirm-modal__body">{children}</div>
      {failed ? <p role="alert" className="inline-alert inline-alert--error">The action could not be confirmed. Close this dialog and check its status before trying again.</p> : null}
      {pending ? <p role="status">Working… Please wait for the result.</p> : null}
      <div className="confirm-modal__actions">
        <button ref={cancel} className="button button--quiet" disabled={pending} onClick={() => setOpen(false)} type="button">Cancel</button>
        <button className={danger ? 'button button--danger' : 'button'} disabled={pending || failed} onClick={() => void confirm()} type="button">{pending ? 'Working…' : confirmLabel}</button>
      </div>
    </dialog>
  </>;
}
